import { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

interface Props {
  currentLogoUrl: string | null;
  pendingFile: File | null;
  deleteRequested: boolean;
  onFileSelect: (f: File) => void;
  onDeleteRequest: () => void;
}

export default function LogoTab({
  currentLogoUrl,
  pendingFile,
  deleteRequested,
  onFileSelect,
  onDeleteRequest,
}: Props) {
  const { t } = useTranslation();
  const fileRef = useRef<HTMLInputElement>(null);
  const [blobPreviewUrl, setBlobPreviewUrl] = useState<string | null>(null);
  const blobUrlRef = useRef<string | null>(null);

  // Revoke blob URL on unmount
  useEffect(() => {
    return () => {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
    };
  }, []);

  const previewUrl = pendingFile ? blobPreviewUrl : deleteRequested ? null : currentLogoUrl;

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files?.[0];
    if (f) {
      if (blobUrlRef.current) URL.revokeObjectURL(blobUrlRef.current);
      const url = URL.createObjectURL(f);
      blobUrlRef.current = url;
      setBlobPreviewUrl(url);
      onFileSelect(f);
    }
    e.target.value = '';
  };

  const handleDeleteRequest = () => {
    if (blobUrlRef.current) {
      URL.revokeObjectURL(blobUrlRef.current);
      blobUrlRef.current = null;
    }
    setBlobPreviewUrl(null);
    onDeleteRequest();
  };

  return (
    <div className="max-w-sm space-y-4">
      {previewUrl ? (
        <div className="space-y-3">
          <img
            src={previewUrl}
            alt="Company logo"
            className="h-24 w-auto object-contain rounded-lg border border-white/10"
          />
          <button
            onClick={handleDeleteRequest}
            className="text-sm text-red-400 hover:text-red-300 transition-colors"
          >
            {t('deleteLogo')}
          </button>
        </div>
      ) : (
        <div className="text-white/40 text-sm">{t('noLogo')}</div>
      )}

      <input
        ref={fileRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        className="hidden"
        onChange={handleFileChange}
      />

      <button
        onClick={() => fileRef.current?.click()}
        className="gradient-btn px-6 py-2 rounded-lg text-white font-medium"
      >
        {t('uploadLogo')}
      </button>

      <p className="text-white/30 text-xs">{t('logoHint')}</p>
    </div>
  );
}
